import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { of } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, map, switchMap } from 'rxjs/operators';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { Company } from 'src/app/models/company.model';
import { MessagingConversation } from 'src/app/models/messaging_conversation.model';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services/api.service';
import { isMessagingConversationPersonal } from '../messaging-conversations.component';

export type AssignMessagingConversationTargetType = 'user' | 'company';

export interface AssignMessagingConversationDialogData {
  conversation: MessagingConversation;
}

export interface AssignMessagingConversationDialogResult {
  target_type: AssignMessagingConversationTargetType;
  user_id?: number;
  company_id?: number;
}

@Component({
  selector: 'app-assign-messaging-conversation-dialog',
  templateUrl: './assign-messaging-conversation-dialog.component.html',
  styleUrl: './assign-messaging-conversation-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatButtonToggleModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
  ],
})
export class AssignMessagingConversationDialogComponent implements OnInit {
  private readonly dialog_ref = inject(
    MatDialogRef<AssignMessagingConversationDialogComponent, AssignMessagingConversationDialogResult | undefined>
  );
  private readonly api = inject(ApiService);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly form_builder = inject(FormBuilder);
  readonly data = inject<AssignMessagingConversationDialogData>(MAT_DIALOG_DATA);

  readonly target_type = signal<AssignMessagingConversationTargetType>(
    isMessagingConversationPersonal(this.data.conversation) ? 'user' : 'company'
  );
  readonly options = signal<AssignMessagingConversationOption[]>([]);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly selected_option = signal<AssignMessagingConversationOption | null>(null);

  readonly form = this.form_builder.group({
    target_type: [this.target_type(), Validators.required],
    search: ['' as string | AssignMessagingConversationOption],
  });

  readonly displayOption = (option: AssignMessagingConversationOption | string | null): string => {
    if (option == null || typeof option === 'string') {
      return option ?? '';
    }

    return option.label;
  };

  ngOnInit(): void {
    this.form
      .get('target_type')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((value: AssignMessagingConversationTargetType | null): void => {
        if (value == null) {
          return;
        }

        this.target_type.set(value);
        this.selected_option.set(null);
        this.options.set([]);
        this.form.controls.search.setValue('', { emitEvent: true });
      });

    this.form
      .get('search')
      ?.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((value: string | AssignMessagingConversationOption | null) => {
          if (value != null && typeof value === 'object') {
            this.selected_option.set(value);

            return of([] as AssignMessagingConversationOption[]);
          }

          this.selected_option.set(null);
          const term = (value ?? '').trim();
          if (term.length < 2) {
            return of([] as AssignMessagingConversationOption[]);
          }

          this.loading.set(true);

          return this.searchOptions(term).pipe(finalize((): void => this.loading.set(false)));
        }),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe((options: AssignMessagingConversationOption[]): void => {
        this.options.set(options);
      });
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const option = event.option.value as AssignMessagingConversationOption;
    this.selected_option.set(option);
  }

  cancel(): void {
    this.dialog_ref.close(undefined);
  }

  confirm(): void {
    const selected = this.selected_option();
    if (selected == null || this.saving()) {
      return;
    }

    this.saving.set(true);

    if (selected.target_type === 'user') {
      this.api.assignMessagingConversationToUser(this.data.conversation.id, { user_id: selected.id }).subscribe({
        next: (): void => {
          this.dialog_ref.close({
            target_type: 'user',
            user_id: selected.id,
          });
        },
        error: (): void => {
          this.saving.set(false);
        },
      });

      return;
    }

    this.api.assignMessagingConversationToCompany(this.data.conversation.id, { company_id: selected.id }).subscribe({
      next: (): void => {
        this.dialog_ref.close({
          target_type: 'company',
          company_id: selected.id,
        });
      },
      error: (): void => {
        this.saving.set(false);
      },
    });
  }

  private searchOptions(term: string) {
    if (this.target_type() === 'user') {
      return this.api
        .getUsers({
          search: term,
          per_page: 20,
          page: 1,
          order_by: 'name:asc',
        })
        .pipe(
          map((response: ArrayResponse<User>): AssignMessagingConversationOption[] =>
            response.data.map(
              (user: User): AssignMessagingConversationOption => ({
                id: user.id,
                target_type: 'user',
                label: formatUserLabel(user),
                subtitle: user.email ?? undefined,
              })
            )
          )
        );
    }

    return this.api
      .getCompanies({
        scopes: 'search:' + term,
        per_page: 20,
        page: 1,
        order_by: 'fact_name:asc',
      })
      .pipe(
        map((response: ArrayResponse<Company>): AssignMessagingConversationOption[] =>
          response.data.map(
            (company: Company): AssignMessagingConversationOption => ({
              id: company.id,
              target_type: 'company',
              label: company.fact_name || `Empresa #${company.id}`,
              subtitle: company.vat || undefined,
            })
          )
        )
      );
  }
}

export interface AssignMessagingConversationOption {
  id: number;
  target_type: AssignMessagingConversationTargetType;
  label: string;
  subtitle?: string;
}

function formatUserLabel(user: User): string {
  return `${user.name} ${user.surnames}`.trim();
}
